(() => {
  const DEFER_CLASS = 'deferred-visibility';
  const VISIBLE_CLASS = 'is-visible';
  const HANDLED_ATTR = 'data-visibility-processed';
  const ROOT_MARGIN = '200px 0px';
  const VIEWPORT_MULTIPLIER = 1.1;
  const SKIP_MATCHERS = [
    '.shopify-section--header',
    '.shopify-section--overlay',
    '.shopify-section--mini-cart',
    '.shopify-section--popup',
    '.shopify-section--drawer'
  ];

  let observer = null;
  let fallbackBound = false;
  const isDesignMode = () =>
    typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
  const pending = new Set();

  function shouldSkip(section) {
    if (!section) return true;
    if (section.getAttribute('data-defer-visibility') === 'false') return true;
    if (SKIP_MATCHERS.some((selector) => section.matches(selector))) return true;

    const inMain = !!section.closest('#main');
    const isFooter = section.classList.contains('shopify-section--footer');
    if (!inMain && !isFooter) return true;

    if (section.offsetParent === null) return true;

    return false;
  }

  function shouldDefer(section) {
    if (!section) return false;
    if (section.getAttribute('data-defer-visibility') === 'true') return true;
    if (shouldSkip(section)) return false;

    const rect = section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (viewportHeight === 0) return false;

    return rect.top > viewportHeight * VIEWPORT_MULTIPLIER;
  }

  function reveal(section) {
    if (!section) return;
    section.classList.remove(DEFER_CLASS);
    section.classList.add(VISIBLE_CLASS);
    if (observer) observer.unobserve(section);
    pending.delete(section);
  }

  function ensureObserver() {
    if (observer || !('IntersectionObserver' in window)) {
      return observer;
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.intersectionRatio === 0) return;
        reveal(entry.target);
      });
    }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });

    return observer;
  }

  function scheduleFallbackReveal() {
    if ('IntersectionObserver' in window) return;
    if (pending.size === 0) return;

    if (fallbackBound) return;
    fallbackBound = true;

    const check = () => {
      pending.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight * VIEWPORT_MULTIPLIER) {
          reveal(section);
        }
      });
    };

    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    window.addEventListener('load', check);
    check();
  }

  function processSection(section) {
    if (!section || section.getAttribute(HANDLED_ATTR) === 'true') return;
    section.setAttribute(HANDLED_ATTR, 'true');

    if (isDesignMode()) return;

    if (!shouldDefer(section)) return;

    section.classList.add(DEFER_CLASS);
    pending.add(section);

    const io = ensureObserver();
    if (io) {
      io.observe(section);
    } else {
      scheduleFallbackReveal();
    }
  }

  function processSections(root = document) {
    const sections = root.querySelectorAll('.shopify-section');
    sections.forEach(processSection);
  }

  function onSectionEvent(event) {
    const target = event?.target;
    if (!target) return;
    if (target.matches('.shopify-section')) {
      target.removeAttribute(HANDLED_ATTR);
      processSection(target);
    } else {
      const section = target.closest('.shopify-section');
      if (section) {
        section.removeAttribute(HANDLED_ATTR);
        processSection(section);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => processSections());
  } else {
    processSections();
  }

  document.addEventListener('shopify:section:load', onSectionEvent, true);
  document.addEventListener('shopify:section:reorder', onSectionEvent, true);
  document.addEventListener('shopify:section:select', onSectionEvent, true);
  document.addEventListener('shopify:section:deselect', onSectionEvent, true);
})();
